import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import type { CredentialSummary } from "@/components/CredentialFields";
import type { Tag } from "@/components/TagsEditor";
import { useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { capitalizeFirst } from "@/lib/capitalizeFirst";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import {
  CONNECTION_METHODS_BY_KIND,
  DEFAULT_PORT_BY_CONNECTION_METHOD,
  credentialTypesForKind,
  isManagedKubernetes,
  type ConnectorOption,
  type GatewayOption,
  type Resource,
  usesOwnIdentity,
  usesPublicCertificate,
  BRIDGED_DATABASE_KINDS,
} from "./shared";

interface UseResourceCrudOptions {
  type: string;
  typeLabelSentence: string;
  kindOptions: string[];
}

export function useResourceCrud({
  type,
  typeLabelSentence,
  kindOptions,
}: UseResourceCrudOptions) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState<Resource[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [gatewayFilterOptions, setGatewayFilterOptions] = useState<
    GatewayOption[]
  >([]);
  const [connectorFilter, setConnectorFilter] = useState("");
  const [connectorFilterOptions, setConnectorFilterOptions] = useState<
    ConnectorOption[]
  >([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formKind, setFormKind] = useState("");
  const [formCredentialType, setFormCredentialType] = useState("");
  const [formCredentialDomain, setFormCredentialDomain] = useState("");
  const [formCredentialUsername, setFormCredentialUsername] = useState("");
  const [formCredentialPassword, setFormCredentialPassword] = useState("");
  const [formCredentialPrivateKey, setFormCredentialPrivateKey] = useState("");
  const [formCredentialPassphrase, setFormCredentialPassphrase] = useState("");
  const [editingCredential, setEditingCredential] =
    useState<CredentialSummary | null>(null);
  const [formAddress, setFormAddress] = useState("");
  const [formClusterName, setFormClusterName] = useState("");
  const [formDatabaseName, setFormDatabaseName] = useState("");
  const [formCertificateAuthority, setFormCertificateAuthority] = useState("");
  const [formPort, setFormPort] = useState<number | "">("");
  const [formConnectorIds, setFormConnectorIds] = useState<string[]>([]);
  const [formConnectorSelections, setFormConnectorSelections] = useState<
    Record<string, ConnectorOption>
  >({});
  const [formTags, setFormTags] = useState<Tag[]>([]);
  const [formConnectorNameFilter, setFormConnectorNameFilter] = useState("");
  const debouncedFormConnectorNameFilter = useDebouncedValue(
    formConnectorNameFilter,
    300,
  );
  const [formConnectorPage, setFormConnectorPage] = useState(1);
  const [formConnectorResults, setFormConnectorResults] = useState<
    ConnectorOption[] | null
  >(null);
  const [formConnectorTotalCount, setFormConnectorTotalCount] = useState(0);
  const [formConnectorsExist, setFormConnectorsExist] = useState(false);
  const [formGatewayIds, setFormGatewayIds] = useState<string[]>([]);
  const [formGatewaySelections, setFormGatewaySelections] = useState<
    Record<string, GatewayOption>
  >({});
  const [formGatewayNameFilter, setFormGatewayNameFilter] = useState("");
  const debouncedFormGatewayNameFilter = useDebouncedValue(
    formGatewayNameFilter,
    300,
  );
  const [formGatewayPage, setFormGatewayPage] = useState(1);
  const [formGatewayResults, setFormGatewayResults] = useState<
    GatewayOption[] | null
  >(null);
  const [formGatewayTotalCount, setFormGatewayTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const derivedConnectionMethod =
    CONNECTION_METHODS_BY_KIND[formKind]?.[0] ?? null;
  const credentialTypeOptions = credentialTypesForKind(formKind);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadResources() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        type,
      });

      if (debouncedNameFilter) {
        params.set("name", debouncedNameFilter);
      }

      if (connectorFilter) {
        params.set("connectorId", connectorFilter);
      }

      if (statusFilter) {
        params.set("status", statusFilter);
      }

      if (kindFilter) {
        params.set("kind", kindFilter);
      }

      const data = await api.get<PagedResult<Resource>>(
        `/organizations/${orgId}/resources?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      if (data) {
        setResources(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      } else {
        setError("Couldn't load resources.");
      }
    }

    void loadResources();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    api,
    orgId,
    type,
    page,
    debouncedNameFilter,
    gatewayFilter,
    connectorFilter,
    statusFilter,
    kindFilter,
    reloadToken,
  ]);

  useEffect(
    // oxlint-disable-next-line react/set-state-in-effect
    () => setPage(1),
    [
      debouncedNameFilter,
      gatewayFilter,
      connectorFilter,
      statusFilter,
      kindFilter,
    ],
  );

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadFilterOptions() {
      const params = new URLSearchParams({ page: "1", pageSize: "100" });

      const [gateways, connectors] = await Promise.all([
        api.get<PagedResult<GatewayOption>>(
          `/organizations/${orgId}/gateways?${params.toString()}`,
        ),
        api.get<PagedResult<ConnectorOption>>(
          `/organizations/${orgId}/connectors?${params.toString()}`,
        ),
      ]);

      if (!cancelled) {
        setGatewayFilterOptions(gateways?.items ?? []);
        setConnectorFilterOptions(connectors?.items ?? []);
      }
    }

    void loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  useEffect(() => {
    if (!accessToken || !orgId || !showModal) {
      return;
    }

    let cancelled = false;

    async function loadFormConnectorResults() {
      const params = new URLSearchParams({
        page: String(formConnectorPage),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedFormConnectorNameFilter) {
        params.set("name", debouncedFormConnectorNameFilter);
      }

      const data = await api.get<PagedResult<ConnectorOption>>(
        `/organizations/${orgId}/connectors?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      setFormConnectorResults(data?.items ?? []);
      setFormConnectorTotalCount(data?.totalCount ?? 0);

      if (!debouncedFormConnectorNameFilter) {
        setFormConnectorsExist((data?.totalCount ?? 0) > 0);
      }
    }

    void loadFormConnectorResults();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    api,
    orgId,
    showModal,
    formConnectorPage,
    debouncedFormConnectorNameFilter,
  ]);

  useEffect(
    // oxlint-disable-next-line react/set-state-in-effect
    () => setFormConnectorPage(1),
    [debouncedFormConnectorNameFilter],
  );

  useEffect(() => {
    if (!accessToken || !orgId || !showModal) {
      return;
    }

    let cancelled = false;

    async function loadFormGatewayResults() {
      const params = new URLSearchParams({
        page: String(formGatewayPage),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedFormGatewayNameFilter) {
        params.set("name", debouncedFormGatewayNameFilter);
      }

      const data = await api.get<PagedResult<GatewayOption>>(
        `/organizations/${orgId}/gateways?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      setFormGatewayResults(data?.items ?? []);
      setFormGatewayTotalCount(data?.totalCount ?? 0);
    }

    void loadFormGatewayResults();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    api,
    orgId,
    showModal,
    formGatewayPage,
    debouncedFormGatewayNameFilter,
  ]);

  useEffect(
    // oxlint-disable-next-line react/set-state-in-effect
    () => setFormGatewayPage(1),
    [debouncedFormGatewayNameFilter],
  );

  const openCreateModal = useCallback(() => {
    setEditingResource(null);
    setFormName("");
    setFormDescription("");
    setFormKind("");
    setFormCredentialType("");
    setFormCredentialDomain("");
    setFormCredentialUsername("");
    setFormCredentialPassword("");
    setFormCredentialPrivateKey("");
    setFormCredentialPassphrase("");
    setEditingCredential(null);
    setFormAddress("");
    setFormClusterName("");
    setFormDatabaseName("");
    setFormCertificateAuthority("");
    setFormPort("");
    setFormConnectorIds([]);
    setFormConnectorSelections({});
    setFormTags([]);
    setFormConnectorNameFilter("");
    setFormConnectorPage(1);
    setFormConnectorsExist(false);
    setFormGatewayIds([]);
    setFormGatewaySelections({});
    setFormGatewayNameFilter("");
    setFormGatewayPage(1);
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (searchParams.get("create") === "1") {
      // oxlint-disable-next-line react/set-state-in-effect
      openCreateModal();
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("create");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams, openCreateModal]);

  function openEditModal(resource: Resource) {
    setEditingResource(resource);
    setFormName(resource.name);
    setFormDescription(resource.description ?? "");
    setFormKind(resource.kind ?? "");
    setFormCredentialType(
      resource.credential?.credentialType ?? "UsernamePassword",
    );
    setFormCredentialDomain(resource.credential?.domain ?? "");
    setFormCredentialUsername(resource.credential?.username ?? "");
    setFormCredentialPassword("");
    setFormCredentialPrivateKey("");
    setFormCredentialPassphrase("");
    setEditingCredential(resource.credential);
    setFormAddress(resource.address);
    setFormClusterName(resource.clusterName ?? "");
    setFormDatabaseName(resource.databaseName ?? "");
    setFormCertificateAuthority("");
    setFormPort(resource.port);
    setFormTags(resource.tags.map((t) => ({ key: t.key, value: t.value })));
    setFormGatewayIds(resource.gateways.map((g) => g.gatewayId));
    setFormGatewaySelections(
      Object.fromEntries(
        resource.gateways.map((g) => [
          g.gatewayId,
          { id: g.gatewayId, name: g.gatewayName, source: g.gatewaySource },
        ]),
      ),
    );
    setFormConnectorIds(resource.connectors.map((c) => c.connectorId));
    setFormConnectorSelections(
      Object.fromEntries(
        resource.connectors.map((c) => [
          c.connectorId,
          { id: c.connectorId, name: c.connectorName },
        ]),
      ),
    );

    setShowModal(true);
  }

  const usesAwsSignIn =
    formKind === "EKS" &&
    (formCredentialType === "AwsIamRole" ||
      formCredentialType === "AwsAccessKey");

  const takesCertificateAuthority =
    (isManagedKubernetes(formKind) ||
      BRIDGED_DATABASE_KINDS.includes(formKind)) &&
    !usesPublicCertificate(formKind, formAddress);

  // The fields are shared between credential types, so a secret typed for one
  // never carries over to another.
  function changeFormCredentialType(value: string) {
    const kept = editingCredential?.credentialType === value;

    setFormCredentialType(value);
    setFormCredentialDomain(kept ? (editingCredential.domain ?? "") : "");
    setFormCredentialUsername(kept ? (editingCredential.username ?? "") : "");
    setFormCredentialPassword("");
    setFormCredentialPrivateKey("");
    setFormCredentialPassphrase("");
  }

  function handleFormKindChange(value: string) {
    setFormKind(value);

    const method = CONNECTION_METHODS_BY_KIND[value]?.[0];
    changeFormCredentialType(
      credentialTypesForKind(value)[0] ?? "UsernamePassword",
    );
    setFormPort((method && DEFAULT_PORT_BY_CONNECTION_METHOD[method]) ?? "");
  }

  function toggleFormConnector(option: ConnectorOption) {
    setFormConnectorIds((current) =>
      current.includes(option.id)
        ? current.filter((id) => id !== option.id)
        : [...current, option.id],
    );
    setFormConnectorSelections((current) => {
      if (current[option.id]) {
        const { [option.id]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [option.id]: option };
    });
  }

  function toggleFormGateway(option: GatewayOption) {
    setFormGatewayIds((current) =>
      current.includes(option.id)
        ? current.filter((id) => id !== option.id)
        : [...current, option.id],
    );
    setFormGatewaySelections((current) => {
      if (current[option.id]) {
        const { [option.id]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [option.id]: option };
    });
  }

  function buildCredentialPayload() {
    if (!formCredentialType) {
      return null;
    }

    if (usesOwnIdentity(formCredentialType)) {
      return {
        credentialType: formCredentialType,
        domain: null,
        username: null,
        password: null,
        privateKey: null,
        passphrase: null,
      };
    }

    if (
      formCredentialType === "BearerToken" ||
      formCredentialType === "GoogleServiceAccount"
    ) {
      return {
        credentialType: formCredentialType,
        domain: null,
        username: null,
        password: formCredentialPassword || null,
        privateKey: null,
        passphrase: null,
      };
    }

    if (
      formCredentialType === "UsernamePassword" ||
      formCredentialType === "DomainUsernamePassword" ||
      formCredentialType === "AwsAccessKey" ||
      formCredentialType === "AzureServicePrincipal"
    ) {
      return {
        credentialType: formCredentialType,
        domain:
          formCredentialType === "DomainUsernamePassword" ||
          formCredentialType === "AzureServicePrincipal"
            ? formCredentialDomain
            : null,
        username: formCredentialUsername,
        password: formCredentialPassword || null,
        privateKey: null,
        passphrase: null,
      };
    }

    return {
      credentialType: formCredentialType,
      domain: null,
      username: formCredentialUsername,
      password: null,
      privateKey: formCredentialPrivateKey || null,
      passphrase: formCredentialPassphrase || null,
    };
  }

  async function applyAssignmentChanges(resource: Resource) {
    const base = `/organizations/${orgId}/resources/${resource.id}`;

    for (const gateway of resource.gateways) {
      if (!formGatewayIds.includes(gateway.gatewayId)) {
        await api.send(
          `${base}/gateways/${gateway.assignmentId}`,
          "DELETE",
          undefined,
          "Couldn't unassign gateway.",
        );
      }
    }

    for (const gatewayId of formGatewayIds) {
      if (!resource.gateways.some((g) => g.gatewayId === gatewayId)) {
        await api.send(
          `${base}/gateways`,
          "POST",
          { gatewayId },
          "Couldn't assign gateway.",
        );
      }
    }

    for (const connector of resource.connectors) {
      if (!formConnectorIds.includes(connector.connectorId)) {
        await api.send(
          `${base}/connectors/${connector.connectorId}`,
          "DELETE",
          undefined,
          "Couldn't unassign connector.",
        );
      }
    }

    for (const connectorId of formConnectorIds) {
      if (!resource.connectors.some((c) => c.connectorId === connectorId)) {
        await api.send(
          `${base}/connectors`,
          "POST",
          { connectorId },
          "Couldn't assign connector.",
        );
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    setSubmitting(true);

    try {
      if (editingResource) {
        await api.send(
          `/organizations/${orgId}/resources/${editingResource.id}`,
          "PATCH",
          {
            name: formName,
            description: formDescription || null,
            type,
            kind: formKind || null,
            connectionMethod: derivedConnectionMethod,
            credential: buildCredentialPayload(),
            address: formAddress,
            port: formPort,
            clusterName: usesAwsSignIn ? formClusterName : null,
            databaseName: BRIDGED_DATABASE_KINDS.includes(formKind)
              ? formDatabaseName
              : null,
            certificateAuthority:
              takesCertificateAuthority && formCertificateAuthority.trim()
                ? formCertificateAuthority
                : null,
            tags: formTags,
          },
          `Couldn't update ${typeLabelSentence}.`,
        );

        await applyAssignmentChanges(editingResource);

        showToast(`${capitalizeFirst(typeLabelSentence)} updated.`, "success");
      } else {
        await api.send(
          `/organizations/${orgId}/resources`,
          "POST",
          {
            name: formName,
            description: formDescription || null,
            type,
            kind: formKind || null,
            connectionMethod: derivedConnectionMethod,
            credential: buildCredentialPayload(),
            address: formAddress,
            port: formPort,
            clusterName: usesAwsSignIn ? formClusterName : null,
            databaseName: BRIDGED_DATABASE_KINDS.includes(formKind)
              ? formDatabaseName
              : null,
            certificateAuthority:
              takesCertificateAuthority && formCertificateAuthority.trim()
                ? formCertificateAuthority
                : null,
            connectorIds: formConnectorIds,
            gatewayIds: formGatewayIds,
            tags: formTags,
          },
          `Couldn't create ${typeLabelSentence}.`,
        );
        showToast(`${capitalizeFirst(typeLabelSentence)} created.`, "success");
      }

      setShowModal(false);
      setReloadToken((token) => token + 1);
    } catch (err) {
      showToast(
        err instanceof Error
          ? err.message
          : `Couldn't save ${typeLabelSentence}.`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(resource: Resource) {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      `${resource.name} will be permanently removed from this organization and can no longer be accessed through Tendra.`,
      { title: `Delete ${resource.name}?`, confirmLabel: "Delete" },
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.send(
        `/organizations/${orgId}/resources/${resource.id}`,
        "DELETE",
        undefined,
        `Couldn't delete ${typeLabelSentence}.`,
      );

      showToast(`${capitalizeFirst(typeLabelSentence)} deleted.`, "success");
      setReloadToken((token) => token + 1);
    } catch (err) {
      showToast(
        err instanceof Error
          ? err.message
          : `Couldn't delete ${typeLabelSentence}.`,
        "error",
      );
    }
  }

  const hasFilters = Boolean(
    debouncedNameFilter ||
    gatewayFilter ||
    connectorFilter ||
    statusFilter ||
    kindFilter,
  );

  function clearFilters() {
    setNameFilter("");
    setGatewayFilter("");
    setConnectorFilter("");
    setStatusFilter("");
    setKindFilter("");
  }

  function retry() {
    setError(null);
    setReloadToken((token) => token + 1);
  }

  return {
    kindOptions,
    typeLabelSentence,
    resources,
    totalCount,
    page,
    setPage,
    error,
    nameFilter,
    setNameFilter,
    gatewayFilter,
    setGatewayFilter,
    gatewayFilterOptions,
    connectorFilter,
    setConnectorFilter,
    connectorFilterOptions,
    statusFilter,
    setStatusFilter,
    kindFilter,
    setKindFilter,
    hasFilters,
    clearFilters,
    retry,
    toast,
    showToast,
    confirmDialog,

    showModal,
    setShowModal,
    editingResource,
    openCreateModal,
    openEditModal,
    handleSubmit,
    handleDelete,
    submitting,

    formName,
    setFormName,
    formDescription,
    setFormDescription,
    formKind,
    handleFormKindChange,
    formCredentialType,
    setFormCredentialType: changeFormCredentialType,
    formCredentialDomain,
    setFormCredentialDomain,
    formCredentialUsername,
    setFormCredentialUsername,
    formCredentialPassword,
    setFormCredentialPassword,
    formCredentialPrivateKey,
    setFormCredentialPrivateKey,
    formCredentialPassphrase,
    setFormCredentialPassphrase,
    editingCredential,
    credentialTypeOptions,
    formAddress,
    formClusterName,
    setFormClusterName,
    formDatabaseName,
    setFormDatabaseName,
    formCertificateAuthority,
    setFormCertificateAuthority,
    setFormAddress,
    formPort,
    setFormPort,
    formTags,
    setFormTags,

    formConnectorIds,
    formConnectorSelections,
    formConnectorNameFilter,
    setFormConnectorNameFilter,
    formConnectorPage,
    setFormConnectorPage,
    formConnectorResults,
    formConnectorTotalCount,
    formConnectorsExist,
    toggleFormConnector,

    formGatewayIds,
    formGatewaySelections,
    formGatewayNameFilter,
    setFormGatewayNameFilter,
    formGatewayPage,
    setFormGatewayPage,
    formGatewayResults,
    formGatewayTotalCount,
    toggleFormGateway,
  };
}
